/**
 * CollapsibleResults Component
 *
 * A reusable component for displaying validation results in a collapsible format.
 * This component serves as a template for displaying any type of validation results.
 *
 * HOW TO ADD NEW RULE CATEGORIES:
 * 1. Pass your validation results to this component
 * 2. Provide a unique title and category identifier
 * 3. The component handles filtering, collapsing, and result display
 *
 * Example usage:
 * <CollapsibleResults
 *   title="Door Clearance"
 *   category="doors"
 *   results={doorValidationResults}
 *   filters={activeFilters}
 *   isExpanded={expandedCategories.has('doors')}
 *   onToggleExpand={() => toggleCategory('doors')}
 *   onViewElement={handleViewElement}
 * />
 */

import React, { useState, useMemo } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import type { ValidationResult } from "../../types/rules";
import { FeedbackButtons } from "../feedback/FeedbackButtons";
import { DetailsModal } from "./DetailsModal";

export interface CollapsibleResultsProps {
  title: string;
  results: ValidationResult[];
  filters: Set<"pass" | "warning" | "fail" | "info">;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onViewElement: (result: ValidationResult) => void;
  showExpandControls?: boolean; // For future nested collapsibles
  regulationCode?: string; // e.g., "ADA", "IBC"
  onShowAll3D?: () => void; // For showing all 3D visualizations at once
  showAll3DButton?: boolean; // Whether to show the "Show all 3D" button
  onManageIssue?: (result: ValidationResult) => void; // For issue management
}

export function CollapsibleResults({
  title,
  results,
  filters,
  isExpanded,
  onToggleExpand,
  onViewElement,
  regulationCode,
  onShowAll3D,
  showAll3DButton,
  onManageIssue,
}: CollapsibleResultsProps) {
  // Modal state for showing details
  const [detailsModal, setDetailsModal] = useState<{
    isOpen: boolean;
    details: Record<string, any> | null;
    title: string;
  }>({
    isOpen: false,
    details: null,
    title: '',
  });

  // Filter results based on active filters
  const filteredResults = useMemo(() => {
    if (filters.size === 0) return results; // Show all if no filters
    return results.filter((result) => filters.has(result.status as any));
  }, [results, filters]);

  // Count results by status
  const counts = useMemo(() => {
    const pass = results.filter((r) => r.status === "pass").length;
    const warning = results.filter((r) => r.status === "warning").length;
    const fail = results.filter((r) => r.status === "fail").length;
    return { pass, warning, fail, total: results.length };
  }, [results]);

  const getStatusIcon = (status: ValidationResult["status"]) => {
    switch (status) {
      case "pass":
        return "Pass";
      case "warning":
        return "Warning";
      case "fail":
        return "Fail";
      case "info":
        return "Info";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Compact Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <button
            onClick={onToggleExpand}
            className="flex items-center gap-2 text-base font-semibold text-gray-900 hover:text-gray-700 transition-colors"
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-gray-600" />
            )}
            <span className="flex items-center gap-2">
              <span>{title}</span>
              {regulationCode && (
                <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                  {regulationCode}
                </span>
              )}
            </span>
          </button>

          {/* Show All 3D Button (if applicable) */}
          {showAll3DButton && onShowAll3D && (
            <button
              onClick={onShowAll3D}
              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1"
              title="View all devices with their OBB and ray visualizations"
            >
              <span>Show all 3D</span>
            </button>
          )}
        </div>
      </div>

      {/* Expanded Table Results Content with adequate height */}
      {isExpanded && (
        <div className="overflow-x-hidden">
          {filteredResults.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <p className="text-sm">
                {filters.size > 0
                  ? "No results match the selected filters"
                  : "No validation results available"}
              </p>
            </div>
          ) : (
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    Status
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Element
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue Description
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResults.map((result, idx) => {
                  const resultId = `${result.elementId}-${result.ruleId}-${idx}`;
                  const hasAssignment =
                    (result.status === "fail" || result.status === "warning") &&
                    (result.assignment?.assignedTo ||
                      result.issuePriority ||
                      result.issueStatus);

                  return (
                    <React.Fragment key={resultId}>
                      <tr
                        className={`group transition-colors ${
                          result.status === "pass"
                            ? "bg-green-50 hover:bg-green-100"
                            : result.status === "warning"
                              ? "bg-yellow-50 hover:bg-yellow-100"
                              : result.status === "fail"
                                ? "bg-red-50 hover:bg-red-100"
                                : result.status === "info"
                                  ? "bg-blue-50 hover:bg-blue-100"
                                  : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-2 py-3">
                          <div className="flex flex-col items-center gap-1">
                            {/* Status badge */}
                            <div
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                result.status === "pass"
                                  ? "bg-green-100 text-green-700 border border-green-300"
                                  : result.status === "warning"
                                    ? "bg-yellow-100 text-yellow-700 border border-yellow-300"
                                    : result.status === "fail"
                                      ? "bg-red-100 text-red-700 border border-red-300"
                                      : result.status === "info"
                                        ? "bg-blue-100 text-blue-700 border border-blue-300"
                                        : "bg-gray-100 text-gray-700 border border-gray-300"
                              }`}
                            >
                                {getStatusIcon(result.status)}
                            </div>

                            {/* Feedback buttons - shown on hover below status */}
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <FeedbackButtons
                                findingId={resultId}
                                ruleId={result.ruleId}
                                elementId={result.elementId}
                                compact={true}
                                showOnHover={false}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="min-w-0">
                            <div
                              className={`text-sm font-medium leading-tight break-words ${
                                result.status === "fail"
                                  ? "text-red-900"
                                  : result.status === "warning"
                                    ? "text-yellow-900"
                                    : result.status === "pass"
                                      ? "text-green-900"
                                      : result.status === "info"
                                        ? "text-blue-900"
                                        : "text-gray-900"
                              }`}
                            >
                              {result.elementName ||
                                `Element ${result.elementId}`}
                            </div>
                            {result.data?.details?.roomName && (
                              <div
                                className={`text-xs font-medium ${
                                  result.status === "fail"
                                    ? "text-red-700"
                                    : result.status === "warning"
                                      ? "text-yellow-700"
                                      : result.status === "pass"
                                        ? "text-green-700"
                                        : result.status === "info"
                                          ? "text-blue-700"
                                          : "text-gray-600"
                                }`}
                              >
                                {result.data.details.roomName}
                              </div>
                            )}
                            <div
                              className={`text-xs ${
                                result.status === "fail"
                                  ? "text-red-600"
                                  : result.status === "warning"
                                    ? "text-yellow-600"
                                    : result.status === "pass"
                                      ? "text-green-600"
                                      : result.status === "info"
                                        ? "text-blue-600"
                                        : "text-gray-500"
                              }`}
                            >
                              ID: {result.elementId}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div
                            className={`text-sm break-words whitespace-normal ${
                              result.status === "fail"
                                ? "text-red-800"
                                : result.status === "warning"
                                  ? "text-yellow-800"
                                  : result.status === "pass"
                                    ? "text-green-800"
                                    : result.status === "info"
                                      ? "text-blue-800"
                                      : "text-gray-900"
                            }`}
                          >
                            {result.message}
                          </div>

                          {result.data?.details && (
                            <button
                              onClick={() =>
                                setDetailsModal({
                                  isOpen: true,
                                  details: result.data.details,
                                  title: `${result.ruleName} - Details`,
                                })
                              }
                              className="mt-1 text-xs text-blue-600 hover:text-blue-500 transition-colors"
                            >
                              ▶ Show Details
                            </button>
                          )}
                        </td>
                        <td className="px-2 py-3 text-center">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => onViewElement(result)}
                              className="px-2 py-1 bg-custom-navy text-white rounded text-xs font-medium hover:bg-custom-dark transition-colors whitespace-nowrap"
                              title="View in 3D model"
                            >
                              View
                            </button>
                            {(result.status === "fail" ||
                              result.status === "warning") &&
                              onManageIssue && (
                                <button
                                  onClick={() => onManageIssue(result)}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors whitespace-nowrap"
                                  title="Assign and manage this issue"
                                >
                                  Manage
                                </button>
                              )}
                            {result.data?.details && (
                              <button
                                onClick={() =>
                                  setDetailsModal({
                                    isOpen: true,
                                    details: result.data.details,
                                    title: `${result.ruleName} - Details`,
                                  })
                                }
                                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 transition-colors whitespace-nowrap"
                              >
                                Details
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Assignment row - only show if there's assignment data */}
                      {hasAssignment && (
                        <tr
                          className={
                            result.status === "fail"
                              ? "bg-red-50"
                              : result.status === "warning"
                                ? "bg-yellow-50"
                                : result.status === "info"
                                  ? "bg-blue-50"
                                : "bg-gray-50"
                          }
                        >
                          <td colSpan={4} className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              {result.assignment?.assignedTo && (
                                <div className="flex items-center gap-1">
                                  <div className="w-5 h-5 rounded-full bg-custom-navy text-white flex items-center justify-center text-xs font-medium">
                                    {result.assignment.assignedTo.name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")
                                      .toUpperCase()
                                      .slice(0, 2)}
                                  </div>
                                  <span className="text-xs text-gray-900 font-medium">
                                    {result.assignment.assignedTo.name}
                                  </span>
                                </div>
                              )}

                              {result.issueStatus && (
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    result.issueStatus === "open"
                                      ? "bg-blue-100 text-blue-700"
                                      : result.issueStatus === "in_progress"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : result.issueStatus === "resolved"
                                          ? "bg-green-100 text-green-700"
                                          : "bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  {result.issueStatus === "in_progress"
                                    ? "In Progress"
                                    : result.issueStatus
                                        .charAt(0)
                                        .toUpperCase() +
                                      result.issueStatus.slice(1)}
                                </span>
                              )}

                              {result.issuePriority && (
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-medium text-white ${
                                    result.issuePriority === "critical"
                                      ? "bg-red-600"
                                      : result.issuePriority === "high"
                                        ? "bg-orange-600"
                                        : result.issuePriority === "medium"
                                          ? "bg-yellow-600"
                                          : "bg-green-600"
                                  }`}
                                >
                                  {result.issuePriority.toUpperCase()}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Details Modal */}
      {detailsModal.details && (
        <DetailsModal
          isOpen={detailsModal.isOpen}
          onClose={() =>
            setDetailsModal({ isOpen: false, details: null, title: '' })
          }
          details={detailsModal.details}
          title={detailsModal.title}
        />
      )}
    </div>
  );
}
